/**
 * Care Concierge advisor API (Feature 106).
 *
 * Every endpoint is scoped to the authenticated advisor — no path parameter names one — so an
 * advisor's engagements are unreachable from another's session by construction.
 */

import { apiClient } from '@/core/api/client';
import { fileNameFromDisposition } from '@/shared/utils/downloadBlob';

const BASE_PATH = '/financial-advisors/me/care-concierge';

export type CreditPoolSource = 'FIRM' | 'USER';
export type CasePayer = 'SPONSOR' | 'SELF_PAY';

/**
 * Requirement v4 item 1. SEND_INVITATION emails the client the booking link; ADVISOR_BOOKS sends
 * nothing and hands the advisor the link instead. A sponsored session is spent either way — the
 * choice is who does the booking, not whether the consultation is committed.
 */
export type SchedulingMode = 'SEND_INVITATION' | 'ADVISOR_BOOKS';

/**
 * One vocabulary shared with the admin surface. OCCURRED is the meeting having happened;
 * COMPLETED is the case being closed afterwards. NOT_COMPLETED is terminal and is what returns a
 * sponsored session.
 */
export type CaseStatus =
  | 'AWAITING_CLIENT'
  | 'SCHEDULED'
  | 'OCCURRED'
  | 'COMPLETED'
  | 'NOT_COMPLETED';

export type AdvisorCreditSummary = {
  /**
   * False until a Care Concierge admin switches this advisor — and their firm — on. Gates the paid
   * consultation card only: the no-fee contact card and the case log both predate this switch and
   * stay, so an advisor never loses sight of consultations they already arranged.
   */
  enabled: boolean;
  firm: { available: boolean; remaining: number; firmName: string | null };
  advisor: { available: boolean; remaining: number };
  /** True only when both pools are funded, so there is a real choice to make. */
  mustChoosePool: boolean;
  /** False when neither pool has a session left; the Sponsor path is blocked. */
  canSponsor: boolean;
  /**
   * The self-pay fee this advisor's clients are quoted, formatted ("$350"). Resolved server-side
   * (advisor → firm → default) so the button and the invitation email never disagree.
   */
  selfPayFee: string;
};

export type CaseOutcome = {
  occurred: boolean;
  occurredOn: string | null;
  /**
   * The Care Concierge team's notes, present only when they ticked the share flag. Null means
   * either no notes or notes they chose not to share — indistinguishable here on purpose.
   */
  notes: string | null;
  referredProviders: string[];
  /**
   * False both when there is no care plan and when the team has not shared the case — the two are
   * indistinguishable here on purpose, same as the notes.
   */
  carePlanAvailable: boolean;
};

export type CareConciergeCase = {
  id: string;
  payer: CasePayer | null;
  creditSource: CreditPoolSource | null;
  inviteeEmail: string | null;
  inviteeName: string | null;
  clientUserId: string | null;
  status: CaseStatus;
  /** When the case was opened. Never moves on resend; null on one nobody was invited to. */
  invitedAt: string | null;
  lastSentAt: string | null;
  sendCount: number;
  /** False on a case the advisor is booking themselves — nothing was emailed. */
  invitationSent: boolean;
  /** Only on the ADVISOR_BOOKS response: where the advisor books. Null on the emailed path. */
  calendlyUrl: string | null;
  /** Booked date and time, filled by the Care Concierge team. */
  scheduledAt: string | null;
  occurredAt: string | null;
  completedAt: string | null;
  notCompletedAt: string | null;
  /** True when this call resent an already-open invitation instead of opening a new one. */
  wasResend: boolean;
  /** True when the email bounced. The engagement survives so it can be resent. */
  deliveryFailed: boolean;
  outcome: CaseOutcome | null;
  /**
   * Set while a session return request is awaiting a decision. The engagement's own status stays
   * AWAITING_CLIENT meanwhile — nothing has been approved yet — so this is what the row reads
   * from to avoid telling the advisor nothing happened.
   */
  pendingReturnRequest: { id: string; requestedAt: string } | null;
  /** Set when the last request was denied, so a refusal is visible rather than silent. */
  deniedReturnRequest: { decidedAt: string | null; decisionNote: string | null } | null;
};

export type CreateCasePayload = {
  payer: CasePayer | null;
  creditSource?: CreditPoolSource;
  clientUserId?: string;
  inviteeEmail: string;
  inviteeName?: string;
  /** Omitted means SEND_INVITATION, which is what the server defaults to. */
  schedulingMode?: SchedulingMode;
};

export async function fetchCareConciergeCredit(): Promise<AdvisorCreditSummary> {
  const response = await apiClient.get<AdvisorCreditSummary>(`${BASE_PATH}/credit`);
  return response.data;
}

export async function fetchCareConciergeCases(): Promise<CareConciergeCase[]> {
  const response = await apiClient.get<CareConciergeCase[]>(`${BASE_PATH}/cases`);
  return response.data;
}

/**
 * Sends the invitation. If one to the same person is already open, the server resends that one
 * rather than opening a second case — check `wasResend` on the result.
 */
export async function createCareConciergeCase(
  payload: CreateCasePayload,
): Promise<CareConciergeCase> {
  const response = await apiClient.post<CareConciergeCase>(`${BASE_PATH}/cases`, payload);
  return response.data;
}

/**
 * Ask for a sponsored session back. Nothing moves until the Care Concierge team approves — the
 * engagement keeps its session in the meantime.
 */
export async function requestSessionReturn(
  caseId: string,
  reason?: string,
): Promise<void> {
  await apiClient.post(
    `${BASE_PATH}/cases/${caseId}/return-request`,
    reason ? { reason } : {},
  );
}

export async function resendCareConciergeInvitation(caseId: string): Promise<CareConciergeCase> {
  const response = await apiClient.post<CareConciergeCase>(
    `${BASE_PATH}/cases/${caseId}/resend`,
    {},
  );
  return response.data;
}

/**
 * The care plan for one of this advisor's cases, as bytes.
 *
 * A blob rather than a URL on purpose: the server streams the file through an authorized endpoint
 * and publishes no link to the object, so revoking the share actually stops the advisor reading
 * it. Callers should revoke the object URL they build from this once the download has started.
 */
export async function fetchCareConciergeCarePlan(
  caseId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const response = await apiClient.get<Blob>(`${BASE_PATH}/cases/${caseId}/care-plan`, {
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFromDisposition(
      (response.headers as Record<string, string | undefined>)['content-disposition'],
      'care-plan.pdf',
    ),
  };
}
